import React, { useEffect, useRef, useState } from "react";

import { ChartEventHandlers, ChartTheme, EChartsOption } from "./types";

type EChartsInstance = {
  dispose: () => void;
  resize: () => void;
  setOption: (option: EChartsOption, notMerge?: boolean) => void;
  showLoading: (type?: string) => void;
  hideLoading: () => void;
  on: (eventName: string, handler: (params: any) => void) => void;
  off: (eventName?: string, handler?: (params: any) => void) => void;
};

interface BaseChartProps {
  option: EChartsOption;
  className?: string;
  style?: React.CSSProperties;
  theme?: ChartTheme;
  eventHandlers?: ChartEventHandlers;
  loading?: boolean;
}

const loadedThemes = new Set<string>();

const BaseChart: React.FC<BaseChartProps> = ({
  option,
  className,
  style,
  theme,
  eventHandlers,
  loading = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<EChartsInstance | null>(null);
  const echartsModuleRef = useRef<any>(null);
  const eventsRef = useRef<ChartEventHandlers | undefined>(eventHandlers);
  const [ready, setReady] = useState(false);

  eventsRef.current = eventHandlers;

  const applyEvents = (): void => {
    const instance = chartInstanceRef.current;
    const handlers = eventsRef.current;

    if (!instance) {
      return;
    }

    instance.off();

    if (!handlers) {
      return;
    }

    Object.entries(handlers).forEach(([event, handler]) => {
      if (typeof handler === "function") {
        instance.on(event, (params: any) => handler(params, instance));
      }
    });
  };

  useEffect(() => {
    let disposed = false;

    const initChart = async (): Promise<void> => {
      if (!containerRef.current) {
        return;
      }

      try {
        if (!echartsModuleRef.current) {
          const mod = await import("echarts");
          echartsModuleRef.current = (mod as any).default ?? mod;
        }

        if (disposed || !containerRef.current) {
          return;
        }

        const echarts = echartsModuleRef.current;

        // 仅在外部以对象形式传入主题时进行注册，避免对特定路径的硬依赖
        if (theme && typeof theme === "object") {
          const themeName = (theme as any).name || "custom-theme";
          if (!loadedThemes.has(themeName)) {
            try {
              echarts.registerTheme(themeName, theme);
              loadedThemes.add(themeName);
            } catch (error) {
              if (process.env.NODE_ENV !== "production") {
                console.warn(`[CompsChart] 注册主题失败:`, error);
              }
            }
          }
        }

        const resolvedTheme =
          typeof theme === "object"
            ? (theme as any).name || "custom-theme"
            : typeof theme === "string" && loadedThemes.has(theme)
            ? theme
            : undefined;

        const instance: EChartsInstance = echarts.init(
          containerRef.current,
          resolvedTheme
        );

        chartInstanceRef.current = instance;
        setReady(true);
        // 调试：检查传递给ECharts的配置
        if (process.env.NODE_ENV === 'development') {
          const klineSeries = (option.series as any[])?.find((s: any) => s.type === 'candlestick');
          if (klineSeries) {
            console.log('[CompsChart] 传递给ECharts的K线图配置:', {
              id: klineSeries.id,
              type: klineSeries.type,
              barWidth: klineSeries.barWidth,
              barMaxWidth: klineSeries.barMaxWidth,
              itemStyle: klineSeries.itemStyle,
              dataLength: klineSeries.data?.length,
              firstDataPoint: klineSeries.data?.[0],
            });
          }
        }
        
        instance.setOption(option, true);
        applyEvents();
      } catch (error) {
        console.error("[CompsChart] 初始化 ECharts 失败", error);
      }
    };

    initChart();

    return () => {
      disposed = true;
      setReady(false);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [theme]);

  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (!instance || !ready) {
      return;
    }

    instance.setOption(option, true);
  }, [option, ready]);

  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (!instance || !ready) {
      return;
    }

    applyEvents();
  }, [eventHandlers, ready]);

  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (!instance) {
      return;
    }

    if (loading) {
      instance.showLoading("default");
    } else {
      instance.hideLoading();
    }
  }, [loading]);

  useEffect(() => {
    const handleResize = (): void => {
      chartInstanceRef.current?.resize();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /** 外部容器（Splitter、flex、侧栏）宽度变化不会触发 window.resize，须监听 DOM 尺寸 */
  useEffect(() => {
    if (!ready) {
      return;
    }
    const el = containerRef.current;
    const inst = chartInstanceRef.current;
    if (!el || !inst) {
      return;
    }
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const ro = new ResizeObserver(() => {
      inst.resize();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [ready]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        minWidth: 0,
        height: "100%",
        minHeight: 240,
        ...style,
      }}
    />
  );
};

export default BaseChart;

